import * as React from 'react';
import { Input, InputAdornment, ModalWindow, UIText2 } from '@cian/ui-kit';
import { IconSearch16 } from '@cian/ui-kit/icons';

import * as styles from './GeoSuggestModal.css';
import { IRegion } from '../../../../types/requestForm';
import { ClearButton } from '../ClearButton';

export interface IGeoSuggestModalProps {
  loading?: boolean;
  onClose(): void;
  onInputChange(text: string): void;
  onSelect(region: IRegion): void;
  open: boolean;
  suggests: IRegion[];
  value: string;
}

export const GeoSuggestModal: React.FC<IGeoSuggestModalProps> = ({
  loading,
  onClose,
  onInputChange,
  onSelect,
  open,
  suggests,
  value,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent, text: string) => {
      onInputChange(text);
    },
    [onInputChange],
  );

  const handleItemClick = React.useCallback(
    (event: React.MouseEvent) => {
      const index = event.currentTarget.getAttribute('data-index') as string;
      const region = suggests[+index];
      onSelect(region);
      onClose();
    },
    [onSelect, onClose, suggests],
  );

  const handleClear = React.useCallback(() => {
    onInputChange('');
    inputRef.current?.focus();
  }, [onInputChange]);

  return (
    <ModalWindow
      size="XS"
      title="Выбор региона"
      open={open}
      onClose={onClose}
      height="100%"
      width="100%"
      maxHeight={600}
      maxWidth={648}
      fixed
    >
      <div className={styles['container']}>
        <div className={styles['input']}>
          <Input
            placeholder="Поиск"
            inputRef={inputRef}
            loading={loading}
            onChange={handleInputChange}
            value={value}
            leftAdornment={
              <InputAdornment>
                <IconSearch16 />
              </InputAdornment>
            }
            rightAdornment={value && <ClearButton onClick={handleClear} />}
          />
        </div>
        {suggests.length > 0 ? (
          <ul className={styles['suggests']} role="listbox" data-testid="SuggestList">
            {suggests.map((region, idx) => {
              return (
                <li
                  key={region.id}
                  className={styles['suggest']}
                  data-index={idx}
                  onClick={handleItemClick}
                  role="option"
                >
                  <UIText2>{region.label}</UIText2>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles['message']}>
            <UIText2 color="gray60_100">Не найдено подходящих регионов</UIText2>
          </div>
        )}
      </div>
    </ModalWindow>
  );
};
